import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';
import AddCommentIcon from '@mui/icons-material/AddComment';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory2';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { alpha } from '@mui/material/styles';
import firebase from '../../utilities/firebase';
import Store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import MarkdownText from '../ChatMarkdown';
import moment from 'moment';

// Dev hits the local Functions emulator on Skyway's dedicated port (5051) — see
// skyway-functions/firebase.json.
const urlBase = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5051/skyway-dev-373d5/us-central1/'
  : 'https://us-central1-skyway-dev-373d5.cloudfunctions.net/';

const TOOL_LABELS = {
  get_service_order: 'Reading the service order',
  get_services: 'Reading the service lines',
  get_parts: 'Reading the parts list',
  get_quality_checks: 'Reading the quality checklist',
  get_time_logs: 'Reading the time logs',
  get_subcontracts: 'Reading subcontracted work',
  get_vehicle: 'Reading the vehicle record',
  get_financials: 'Reading costs & payments',
  get_notes: 'Reading staff notes',
};

const toolLabel = (name) => TOOL_LABELS[name] || `Using ${name}`;

const toolIcon = (name) => {
  if (name === 'get_parts') return <InventoryIcon sx={{ fontSize: 12 }} />;
  if (name === 'get_services' || name === 'get_subcontracts') return <BuildIcon sx={{ fontSize: 12 }} />;
  if (name === 'get_quality_checks') return <FactCheckIcon sx={{ fontSize: 12 }} />;
  if (name === 'get_time_logs') return <ScheduleIcon sx={{ fontSize: 12 }} />;
  return <StorageIcon sx={{ fontSize: 12 }} />;
};

// Shown on an empty chat so staff can see what this thing is good at.
const SUGGESTIONS = [
  'Which services can we start with the parts that have arrived?',
  'What parts are still outstanding, and when were they ordered?',
  'What quality checks are still open?',
  'How many hours have been clocked on this order?',
];

// ── SSE streaming client for the serviceOrderChat Cloud Function ──
async function streamServiceOrderChat({ orderId, messages, onText, onTool, onError, onDone }) {
  let token = null;
  try {
    const user = firebase.auth && firebase.auth().currentUser;
    if (user) token = await user.getIdToken();
  } catch (e) { /* unauthenticated — backend will reject if it needs to */ }

  let res;
  try {
    res = await fetch(`${urlBase}serviceOrderChat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ orderId, messages }),
    });
  } catch (e) {
    onError && onError(new Error('Could not reach the chat service.'));
    return;
  }

  if (!res.ok || !res.body) {
    onError && onError(new Error(`Chat request failed (${res.status}).`));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop();
    for (const chunk of chunks) {
      const dataLine = chunk.split('\n').find(l => l.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      let evt;
      try { evt = JSON.parse(payload); } catch (e) { continue; }
      if (evt.type === 'text') onText && onText(evt.text);
      else if (evt.type === 'tool') onTool && onTool(evt.name);
      else if (evt.type === 'error') onError && onError(new Error(evt.error || 'Chat error'));
      else if (evt.type === 'done') onDone && onDone();
    }
  }
}

// ── Background chat sessions ───────────────────────────────────────────────
// Mirrors CarModal/Chat.js: a chat keeps streaming — and saves itself — even if
// the modal closes mid-answer. Sessions are keyed by service-order id (only one
// in-flight chat per order, since the composer locks while streaming).
const liveChats = new Map();

const notifyChat = (session) => {
  if (session.onUpdate) {
    try { session.onUpdate(session); } catch (e) { /* component gone — ignore */ }
  }
};

function startChatStream({ orderId, baseMsgs, existingChatId, isNew, title }) {
  const now = moment().format('YYYY/MM/DD HH:mm');
  const session = {
    orderId,
    chatId: existingChatId || null,
    messages: [...baseMsgs, { role: 'assistant', text: '', tools: [], timestamp: now, streaming: true }],
    streaming: true,
    activeTool: null,
    onUpdate: null,
  };
  liveChats.set(orderId, session);

  let text = '';
  let tools = [];
  const patchAssistant = (patch) => {
    const msgs = session.messages.slice();
    msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], ...patch };
    session.messages = msgs;
    notifyChat(session);
  };

  (async () => {
    await streamServiceOrderChat({
      orderId,
      messages: baseMsgs.map(m => ({ role: m.role, text: m.text })),
      onText: (t) => { text += t; patchAssistant({ text }); },
      onTool: (name) => {
        if (!tools.includes(name)) tools = [...tools, name];
        session.activeTool = name;
        patchAssistant({ tools });
      },
      onError: (err) => {
        text = text ? text + `\n\n_(Error: ${err.message})_` : `Sorry — something went wrong: ${err.message}`;
        patchAssistant({ text });
        StateManager.setAlertAndOpen(`Chat error: ${err.message}`, 'error');
      },
    });

    session.streaming = false;
    session.activeTool = null;
    session.messages = [
      ...baseMsgs,
      { role: 'assistant', text, tools, timestamp: moment().format('YYYY/MM/DD HH:mm') },
    ];

    try {
      if (!session.chatId) {
        const created = await Store.createServiceOrderChat(orderId, title);
        session.chatId = created && created.id;
      }
      if (session.chatId) {
        await Store.saveServiceOrderChat(orderId, session.chatId, { messages: session.messages, title: isNew ? title : undefined });
      }
    } catch (e) {
      StateManager.setAlertAndOpen('Could not save chat history.', 'warning');
    }

    notifyChat(session);
    // Release a finished session if nothing consumes it within a few minutes, so
    // a stale "done" chat never resurfaces on a much later visit.
    setTimeout(() => { if (liveChats.get(orderId) === session) liveChats.delete(orderId); }, 5 * 60 * 1000);
  })();

  return session;
}

export default function ChatTab({ order, stockNumber }) {
  const orderId = stockNumber;

  const [chats, setChats] = React.useState([]);
  const [activeChatId, setActiveChatId] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [streaming, setStreaming] = React.useState(false);
  const [activeTool, setActiveTool] = React.useState(null);

  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const scrollRef = React.useRef(null);
  const atBottomRef = React.useRef(true);
  const rootRef = React.useRef(null);
  const [availH, setAvailH] = React.useState(null);

  // Size the chat to fill the modal viewport exactly, so the composer is always
  // pinned at the bottom and only the transcript scrolls.
  const recomputeHeight = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    let sc = root.parentElement;
    while (sc) {
      const oy = window.getComputedStyle(sc).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      sc = sc.parentElement;
    }
    const target = sc || document.documentElement;
    const cRect = target.getBoundingClientRect();
    const rRect = root.getBoundingClientRect();
    const offset = (rRect.top - cRect.top) + (target.scrollTop || 0);
    const gap = 20; // leaves room for the tab-content bottom padding
    const h = target.clientHeight - offset - gap;
    setAvailH(prev => {
      const next = Math.max(360, Math.round(h));
      return prev === next ? prev : next;
    });
  }, []);

  React.useLayoutEffect(() => { recomputeHeight(); }, [recomputeHeight, order.status]);

  React.useEffect(() => {
    recomputeHeight();
    window.addEventListener('resize', recomputeHeight);
    let ro;
    const root = rootRef.current;
    let sc = root && root.parentElement;
    while (sc) {
      const oy = window.getComputedStyle(sc).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      sc = sc.parentElement;
    }
    if (sc && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(recomputeHeight);
      ro.observe(sc);
    }
    return () => {
      window.removeEventListener('resize', recomputeHeight);
      if (ro) ro.disconnect();
    };
  }, [recomputeHeight]);

  const loadChats = React.useCallback(async () => {
    if (!orderId) return;
    try {
      const list = await Store.getServiceOrderChats(orderId);
      setChats(list);
    } catch (e) { /* listing failure is non-fatal */ }
  }, [orderId]);

  React.useEffect(() => { loadChats(); }, [loadChats]);

  // Mirror a background chat session into this component's view. Guards against
  // a stale session updating after the user has switched orders / unmounted.
  const applySession = React.useCallback((s) => {
    if (!mountedRef.current || !s || s.orderId !== orderId) return;
    setMessages(s.messages);
    setStreaming(s.streaming);
    setActiveTool(s.activeTool);
    if (s.chatId) setActiveChatId(s.chatId);
    if (!s.streaming) loadChats();
  }, [orderId, loadChats]);

  // On mount / order change, re-attach to any in-flight (or just-finished) chat
  // started for this order while we were away, so the response is present.
  React.useEffect(() => {
    const session = liveChats.get(orderId);
    if (!session) return;
    session.onUpdate = applySession;
    applySession(session);
    if (!session.streaming) liveChats.delete(orderId);
    return () => { if (session.onUpdate === applySession) session.onUpdate = null; };
  }, [orderId, applySession]);

  // Auto-scroll only when the user is already near the bottom — don't yank them
  // down if they've scrolled up to read while it streams in.
  React.useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeTool]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const startNewChat = () => {
    if (streaming) return;
    liveChats.delete(orderId);
    setActiveChatId(null);
    setMessages([]);
    setInput('');
  };

  const openChat = (chat) => {
    if (streaming) return;
    liveChats.delete(orderId);
    setActiveChatId(chat.id);
    setMessages(chat.messages || []);
  };

  const sendText = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed || streaming || !orderId) return;

    const now = moment().format('YYYY/MM/DD HH:mm');
    const baseMsgs = [...messages, { role: 'user', text: trimmed, timestamp: now }];

    atBottomRef.current = true; // jump to the latest when the user sends
    setInput('');

    const session = startChatStream({
      orderId,
      baseMsgs,
      existingChatId: activeChatId,
      isNew: !activeChatId,
      title: trimmed.slice(0, 48),
    });
    session.onUpdate = applySession;
    applySession(session);
  };

  const handleKeyDown = (e) => {
    // While streaming, let Enter insert a newline so the user can keep composing.
    if (e.key === 'Enter' && !e.shiftKey && !streaming) { e.preventDefault(); sendText(input); }
  };

  const fmtChatTime = (ts) => {
    if (!ts) return '';
    const m = ts.toDate ? moment(ts.toDate()) : moment(ts);
    if (!m.isValid()) return '';
    if (moment().diff(m, 'days') < 1) return m.format('h:mm a');
    if (moment().diff(m, 'days') < 7) return m.format('ddd');
    return m.format('MMM D');
  };

  return (
    <Box ref={rootRef} sx={{ mx: -3, mt: -1, display: 'flex', height: availH ? `${availH}px` : '72vh', minHeight: 360 }}>
      {/* ── Past chats sidebar ── */}
      <Box sx={{ width: { xs: 132, sm: 220 }, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', flexShrink: 0, bgcolor: 'grey.50' }}>
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" variant="outlined" startIcon={<AddCommentIcon />} onClick={startNewChat} disabled={streaming}>
            New chat
          </Button>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5, pb: 1 }}>
          {chats.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', textAlign: 'center', mt: 2, px: 1 }}>
              No past chats yet
            </Typography>
          ) : chats.map(chat => {
            const selected = chat.id === activeChatId;
            return (
              <Box
                key={chat.id}
                onClick={() => openChat(chat)}
                sx={{
                  px: 1, py: 0.75, mb: 0.25, borderRadius: 1, cursor: 'pointer',
                  bgcolor: selected ? 'primary.main' : 'transparent',
                  color: selected ? 'primary.contrastText' : 'text.primary',
                  '&:hover': { bgcolor: selected ? 'primary.main' : (t) => alpha(t.palette.action.hover, 0.6) },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 13, opacity: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" noWrap sx={{ fontWeight: selected ? 600 : 500, fontSize: '0.8rem' }}>
                    {chat.title || 'Chat'}
                  </Typography>
                </Box>
                <Typography variant="caption" noWrap sx={{ display: 'block', pl: 2.25, opacity: 0.7, fontSize: 10 }}>
                  {(chat.createdBy ? chat.createdBy.split(' ')[0] + ' · ' : '') + fmtChatTime(chat.updatedAt)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Conversation pane ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: 2 }}>
          {messages.length === 0 ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary', px: 2 }}>
              <SmartToyIcon sx={{ fontSize: 44, color: 'primary.light', mb: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Ask about service order {orderId}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 460 }}>
                I can read the services, parts, quality checklist, time logs, notes, costs and the vehicle on this order.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', maxWidth: 520 }}>
                {SUGGESTIONS.map((s) => (
                  <Chip key={s} label={s} size="small" variant="outlined" onClick={() => sendText(s)} sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                ))}
              </Box>
            </Box>
          ) : messages.map((msg, i) => {
            const isUser = msg.role === 'user';
            return (
              <Box key={i} sx={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', gap: 1.25, mb: 2, alignItems: 'flex-start' }}>
                {!isUser && (
                  <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                    <SmartToyIcon sx={{ fontSize: 17, color: 'white' }} />
                  </Box>
                )}
                <Box sx={{ maxWidth: isUser ? '78%' : '88%', minWidth: 0 }}>
                  {!isUser && msg.tools && msg.tools.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                      {msg.tools.map((t, ti) => (
                        <Box key={ti} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: 1, bgcolor: 'grey.100', border: 1, borderColor: 'divider' }}>
                          {toolIcon(t)}
                          <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.secondary' }}>{toolLabel(t)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {isUser ? (
                    <Paper elevation={0} sx={{ px: 1.75, py: 1.1, bgcolor: 'primary.main', color: 'white', borderRadius: '14px 4px 14px 14px', display: 'inline-block' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>{msg.text}</Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ bgcolor: 'grey.100', borderRadius: '4px 14px 14px 14px', px: 1.75, py: 1.1 }}>
                      {msg.text
                        ? <MarkdownText text={msg.text} />
                        : (msg.streaming
                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                <CircularProgress size={13} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {activeTool ? toolLabel(activeTool) + '…' : 'Thinking…'}
                                </Typography>
                              </Box>
                            : <Typography variant="body2" sx={{ color: 'text.disabled' }}>(no response)</Typography>)
                      }
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Composer ── */}
        <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder="Ask about this service order… (↵ to send, ⇧↵ for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Tooltip title="Send">
            <span>
              <IconButton color="primary" onClick={() => sendText(input)} disabled={!input.trim() || streaming} sx={{ flexShrink: 0, mb: 0.25 }}>
                {streaming ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
